import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  PageBody,
  type DataTableColumn,
} from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import {
  listUnansweredConversations,
  retryConversation,
  type UnansweredConversation,
} from '@/lib/unansweredConversations.ts';

export const Route = createFileRoute('/_app/admin/unanswered')({
  staticData: { orgScope: 'optional' },
  component: UnansweredConversationsPage,
});

function userLabel(conversation: UnansweredConversation): string {
  return (
    conversation.user_name || conversation.user_email || conversation.user_id.slice(0, 8)
  );
}

/** How long the user has been waiting, in the coarsest unit that still says it. */
function relativeAge(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The conversations nobody answered, and the one button that fixes them.
 *
 * A recovery tool rather than a report: every row here is a person still
 * waiting, so the list is short, unpaged and unsorted — the server hands back
 * the hundred most recent and the whole page is the retry button.
 */
export function UnansweredConversationsPage() {
  const [retried, setRetried] = useState<string[]>([]);
  const organisationId = useOrganisationFilter()?.id;

  const { data, isPending, error } = useQuery({
    queryKey: ['unansweredConversations', organisationId ?? null],
    queryFn: () => listUnansweredConversations(organisationId),
  });

  const retry = useMutation({
    mutationFn: retryConversation,
    /*
     * The row stays where it is and says "Retried". Re-reading the list here
     * would take it away before the pipeline has answered anything, which
     * looks like the problem is solved when it is only under way.
     */
    onSuccess: (_result, conversationId) => {
      setRetried((current) => [...current, conversationId]);
    },
  });

  const columns: DataTableColumn<UnansweredConversation>[] = [
    {
      key: 'user',
      header: 'User',
      render: (conversation) => (
        <div>
          <div className='font-semibold text-ink'>{userLabel(conversation)}</div>
          {conversation.user_email && conversation.user_name && (
            <div className='text-xs text-ink-muted'>{conversation.user_email}</div>
          )}
        </div>
      ),
    },
    { key: 'channel', header: 'Channel' },
    {
      key: 'last_message_at',
      header: 'Last message',
      render: (conversation) => (
        <div className='whitespace-nowrap'>
          <div className='text-ink'>{relativeAge(conversation.last_message_at)}</div>
          <div className='text-xs text-ink-muted'>
            {formatDateTime(conversation.last_message_at)}
          </div>
        </div>
      ),
    },
    {
      key: 'snippet',
      header: 'Message',
      render: (conversation) => (
        <span className='block max-w-100 truncate'>{conversation.snippet}</span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title='Unanswered Conversations'
        subtitle='Conversations where the user’s last message never got a reply — older than 15 minutes, within the last 14 days. Retry re-runs the conversation through the pipeline so the user gets an answer.'
      />

      <PageBody>
        <DataTable<UnansweredConversation>
          label='Unanswered conversations'
          columns={columns}
          rows={data ?? []}
          rowKey={(conversation) => conversation.conversation_id}
          loading={isPending}
          error={error ? 'Failed to load unanswered conversations' : undefined}
          actions={(conversation) =>
            retried.includes(conversation.conversation_id) ? (
              <Badge tone='success'>Retried</Badge>
            ) : (
              <Button
                size='sm'
                variant='accent'
                loading={
                  retry.isPending && retry.variables === conversation.conversation_id
                }
                onClick={() => retry.mutate(conversation.conversation_id)}
              >
                Retry
              </Button>
            )
          }
          emptyState={<EmptyState title='No unanswered conversations.' />}
        />
      </PageBody>
    </>
  );
}
